import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { AccountType, UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { Account } from '../../../models/account';
import { AccountApi } from '../../../api/account-api';
import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { PoolAutocompleteListAction } from '../pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { User } from '../../../models/user';

export interface PartialAccount extends BaseAutocompleteSuggestionType {
    name: string;
    city: string;
}

interface PoolAccountAutocompleteListProps {
    user: User;
}

class PoolAccountAutocompleteListBase extends PoolFormAutocompleteList<
    Account,
    PartialAccount,
    PoolAccountAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Accounting, UserRole.Sales];
    private api = new AccountApi(API_BASE_URL);
    // A locked partner is one nobody may work with any more, so it is not
    // offered here — in the documents it stays visible where it already
    // stands (LST-S14, #2232).
    private searchParams = new URLSearchParams({
        limit: '40',
        'filter[type]': 'customer,supplier',
        'filter[isLocked]': 'false',
    });
    private apiAbortController?: AbortController;

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.customers"
            url={urljoin(BASE_URL, ROUTES.contactsAndOrganizations.customers.new)}
        />
    );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialAccount) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.cget(this.searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: Account[]): PartialAccount[] => {
        return items.map(account => {
            const baseRoute =
                account.type === AccountType.Customer
                    ? ROUTES.contactsAndOrganizations.customers
                    : ROUTES.contactsAndOrganizations.suppliers;
            const editLink = urljoin(BASE_URL, baseRoute.detail.replace(':id', account.id.toString()));
            return {
                id: padZeroLeft(account.id, 5),
                name: account.name,
                city: account.getCity() || '',
                tags: account.getTags(),
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });
    };

    public getDisplayValue(account: Account) {
        return account.getDisplayString();
    }
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolAccountAutocompleteList = connect(mapStateToProps, {})(PoolAccountAutocompleteListBase);
